/**
 * Scoped delta pull and oldest-first outbox flush for the field path.
 *
 * RxDB is the offline source of truth. The service worker deliberately does
 * not cache authenticated API responses because those responses are not safe
 * to share between users or organisations.
 */
import { getDb } from './db'
import type { GranulaDb } from './db'
import { cursorKey, getOfflineScope, type OfflineScope } from './scope'
import { authStore } from '@/store/auth'
import { orgStore } from '@/store/org'
import { apiClient } from '@/utils/api'
import { financeApi } from '@/features/finance'
import { notifyExpensesChanged } from './expenses'
import type { ExpenseDocument, OutboxDocument, TaskDocument } from './schema'
import type { RxDocument } from 'rxdb'

const MAX_ATTEMPTS = 5
let syncInFlight: Promise<void> | null = null

export type ServerTask = {
  id: number
  public_id?: string | null
  org_id: number
  title?: string
  description?: string
  priority?: TaskDocument['priority']
  status?: string
  due_date?: string | null
  assigned_to?: number | null
  project_id?: number | null
  phase_id?: number | null
  created_by?: number
  labels?: string[]
  approval_status?: string | null
  approval_notes?: string | null
  created_at?: string
  updated_at?: string
}

type SyncResponse = { tasks?: ServerTask[]; cursor?: string }

function getHeaders(): Record<string, string> {
  const token = authStore.getState().accessToken
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

function notifyTasksChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('granula:tasks-changed'))
}

// Pull (server → local)
export async function pull(): Promise<void> {
  const scope = getOfflineScope()
  if (!scope) return

  try {
    const cursor = localStorage.getItem(cursorKey(scope))
    const url = cursor
      ? `/api/work/sync?since=${encodeURIComponent(cursor)}`
      : '/api/work/sync'
    const res = await fetch(url, { headers: getHeaders() })
    if (!res.ok) return

    const data = await res.json() as SyncResponse
    await cacheServerTasks(data.tasks ?? [], scope)
    if (data.cursor) localStorage.setItem(cursorKey(scope), data.cursor)
  } catch {
    // Network down or malformed response: leave the cursor unchanged.
  }
}

/** Warm the scoped RxDB mirror from an online task-list response. */
export async function cacheServerTasks(tasks: ServerTask[], scope = getOfflineScope()): Promise<void> {
  if (!scope) return
  const db = await getDb()
  let changed = false
  for (const task of tasks) {
    // Keep the client-side guard even though the endpoint is tenant-scoped.
    if (task.org_id !== scope.orgId) continue
    const existing = await db.tasks.findOne(`task_${task.id}`).exec()
    // A pending local write is authoritative until its outbox operation is
    // accepted; a delta must never silently erase that optimistic state.
    if (existing?.dirty) continue
    const next = serverTaskToDoc(task, scope)
    const taskChanged = !existing || [
      'public_id', 'title', 'description', 'priority', 'status', 'due_date',
      'assigned_to', 'project_id', 'phase_id', 'created_by', 'created_at',
      'updated_at', 'approval_status', 'approval_notes', 'sync_status',
      'sync_error',
    ].some((field) => existing[field as keyof TaskDocument] !== next[field as keyof TaskDocument])
      || JSON.stringify(existing.labels) !== JSON.stringify(next.labels)
    if (!taskChanged) continue
    await db.tasks.upsert(next)
    changed = true
  }
  if (changed) notifyTasksChanged()
}

// Push (outbox → server)
export async function flush(): Promise<void> {
  const scope = getOfflineScope()
  if (!scope) return
  const db = await getDb()
  const snapshots = await db.outbox.find({ sort: [{ created_at: 'asc' }] }).exec()

  for (const snapshot of snapshots) {
    // Re-read after every preceding operation. Create-task reconciliation may
    // re-home child ops from a temp RxDB id to the server task id.
    const op = await db.outbox.findOne(snapshot.op_id).exec()
    if (!op || op.attempts >= MAX_ATTEMPTS) continue

    if (op.op_type === 'create_expense') {
      await flushOfflineExpense(db, op)
      continue
    }

    const linkedTask = await db.tasks.findOne(op.rxdb_id).exec()
    if (op.op_type !== 'create_task' && linkedTask?.server_id == null) {
      // The create operation is older and will re-home this operation after it
      // succeeds. Do not burn retries while the parent is still pending.
      continue
    }

    try {
      const payload = JSON.parse(op.payload) as Record<string, unknown>
      const { url, method } = await resolveEndpoint(db, op.op_type, op.server_id, op.rxdb_id)
      const res = await fetch(url, {
        method,
        headers: { ...getHeaders(), 'X-Op-Id': op.op_id },
        body: JSON.stringify(payload),
      })
      const responseBody = await readJson(res)

      if (!res.ok) {
        await recordTaskFailure(db, op, errorMessage(responseBody, res.status))
        continue
      }

      const serverTask = isServerTask(responseBody) ? responseBody : null
      if (serverTask) {
        if (op.op_type === 'create_task') {
          await resolveTemp(db, op.rxdb_id, serverTask, scope)
        } else {
          await db.tasks.upsert(serverTaskToDoc(serverTask, scope))
        }
      }
      await op.remove()
      notifyTasksChanged()
    } catch (error) {
      await recordTaskFailure(db, op, error instanceof Error ? error.message : 'task_sync_failed')
    }
  }
}

async function flushOfflineExpense(db: GranulaDb, op: RxDocument<OutboxDocument>): Promise<void> {
  const doc = await db.expenses.findOne(op.rxdb_id).exec()
  if (!doc) {
    await op.remove()
    return
  }
  const activeUserId = authStore.getState().user?.id
  const activeOrgId = orgStore.getState().org?.id
  if (!activeUserId || !activeOrgId || doc.user_id !== activeUserId || doc.org_id !== activeOrgId) return

  const now = () => new Date().toISOString()
  try {
    const payload = JSON.parse(op.payload) as Parameters<typeof financeApi.createExpense>[0]
    const expense = await financeApi.createExpense(payload, op.op_id)
    if (!expense?.id || expense.id < 1) throw new Error('expense_sync_missing_server_id')

    await doc.incrementalPatch({ server_id: expense.id, sync_status: 'syncing', last_error: null, updated_at: now(), dirty: true })
    await op.incrementalPatch({ server_id: expense.id })

    const currentReceipts = [...(doc.receipts as ExpenseDocument['receipts'])]
    for (const receipt of currentReceipts) {
      if (receipt.synced) continue
      const attachment = doc.getAttachment(receipt.attachment_id)
      if (!attachment) throw new Error(`missing_offline_receipt_${receipt.file_name}`)
      const blob = await attachment.getData()
      const presigned = await financeApi.presignReceipt(expense.id, {
        file_name: receipt.file_name,
        content_type: receipt.content_type,
        size_bytes: receipt.size_bytes,
      })
      await apiClient.uploadToPresignedUrl(presigned.upload_url, blob)
      await financeApi.confirmReceipt(expense.id, {
        file_key: presigned.file_key,
        file_name: receipt.file_name,
        content_type: receipt.content_type,
        size_bytes: receipt.size_bytes,
      }, receipt.op_id)
      const nextReceipts = currentReceipts.map((candidate) => (
        candidate.op_id === receipt.op_id ? { ...candidate, synced: true } : candidate
      ))
      currentReceipts.splice(0, currentReceipts.length, ...nextReceipts)
      await doc.incrementalPatch({ receipts: currentReceipts, updated_at: now() })
    }

    // Both were patched above, so `doc` and `op` are stale revisions here:
    // a plain remove() is refused with CONFLICT and the claim, already on the
    // server, is left showing "Sync failed". incrementalRemove() works on
    // the latest revision.
    await doc.incrementalRemove()
    await op.incrementalRemove()
    notifyExpensesChanged()
    window.dispatchEvent(new Event('granula:finance-changed'))
  } catch (error) {
    const rawMessage = error instanceof Error ? error.message : 'expense_sync_failed'
    const message = /budget|allocation|project plan|expense coding/i.test(rawMessage)
      ? `Project budget coding needs attention: ${rawMessage}`
      : rawMessage
    await doc.incrementalPatch({ sync_status: 'sync_failed', last_error: message, updated_at: now() })
    await op.incrementalPatch({ attempts: op.attempts + 1, last_attempt_at: now(), last_error: message })
    notifyExpensesChanged()
  }
}

export async function syncNow(): Promise<void> {
  if (syncInFlight) return syncInFlight
  syncInFlight = (async () => {
    await pull()
    await flush()
  })().finally(() => { syncInFlight = null })
  return syncInFlight
}

async function resolveEndpoint(
  db: GranulaDb,
  opType: string,
  serverId: number | null,
  rxdbId: string,
): Promise<{ url: string; method: string }> {
  switch (opType) {
    case 'create_task': return { url: '/api/work/tasks', method: 'POST' }
    case 'submit_task': return { url: `/api/work/tasks/${serverId}/submit`, method: 'POST' }
    case 'approve_task': return { url: `/api/work/tasks/${serverId}/approve`, method: 'POST' }
    case 'reject_task': return { url: `/api/work/tasks/${serverId}/reject`, method: 'POST' }
    case 'update_task':
    default: {
      const doc = await db.tasks.findOne(rxdbId).exec()
      if (!doc?.public_id && !serverId) throw new Error('task_sync_missing_server_id')
      return { url: `/api/work/tasks/${doc?.public_id ?? serverId}`, method: 'PATCH' }
    }
  }
}

function serverTaskToDoc(task: ServerTask, scope: OfflineScope): TaskDocument {
  return {
    rxdb_id: `task_${task.id}`,
    local_id: null,
    server_id: task.id,
    public_id: task.public_id ?? null,
    org_id: scope.orgId,
    user_id: scope.userId,
    title: task.title ?? '',
    description: task.description ?? '',
    priority: task.priority ?? 'normal',
    status: (task.status ?? 'not_started') as TaskDocument['status'],
    due_date: task.due_date ?? null,
    assigned_to: task.assigned_to ?? null,
    project_id: task.project_id ?? null,
    phase_id: task.phase_id ?? null,
    created_by: task.created_by ?? scope.userId,
    labels: task.labels ?? [],
    approval_status: task.approval_status ?? null,
    approval_notes: task.approval_notes ?? null,
    created_at: task.created_at ?? new Date().toISOString(),
    updated_at: task.updated_at ?? new Date().toISOString(),
    dirty: false,
    sync_status: 'synced',
    sync_error: null,
  }
}

async function resolveTemp(db: GranulaDb, tempRxdbId: string, serverTask: ServerTask, scope: OfflineScope) {
  const childOps = await db.outbox.find({ selector: { rxdb_id: tempRxdbId } }).exec()
  const canonical = serverTaskToDoc(serverTask, scope)
  const tempDoc = await db.tasks.findOne(tempRxdbId).exec()
  if (tempDoc) await tempDoc.remove()
  await db.tasks.upsert(canonical)
  for (const child of childOps) {
    if (child.op_type === 'create_task') continue
    await child.incrementalPatch({ rxdb_id: canonical.rxdb_id, server_id: serverTask.id })
  }
}

async function recordTaskFailure(db: GranulaDb, op: RxDocument<OutboxDocument>, message: string) {
  const now = new Date().toISOString()
  await op.incrementalPatch({ attempts: op.attempts + 1, last_attempt_at: now, last_error: message })
  const doc = await db.tasks.findOne(op.rxdb_id).exec()
  if (doc) await doc.incrementalPatch({ sync_status: 'sync_failed', sync_error: message, updated_at: now })
}

async function readJson(res: Response): Promise<unknown> {
  try { return await res.json() } catch { return null }
}

function isServerTask(value: unknown): value is ServerTask {
  return typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'number'
}

function errorMessage(value: unknown, status: number): string {
  if (typeof value === 'object' && value !== null && 'message' in value && typeof value.message === 'string') return value.message
  if (typeof value === 'object' && value !== null && 'error' in value && typeof value.error === 'string') return value.error
  return `task_sync_http_${status}`
}
